import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DistanceTo from '@/components/Resources/DistanceTo/DistanceTo';
import StarRating from '@/components/Resources/StarRating/StarRating';
import WhatToDoNext from '@/components/Resources/WhatToDoNext/WhatToDoNext';
import UtilizationQuestion from '@/components/Resources/UtilizationQuestion/UtilizationQuestion';
import Button from '@/components/Button/Button';
import ResourceIcon from '@/components/Resources/ResourceIcon/ResourceIcon';
import ResourceCardBanner from '@/components/Resources/ResourceCardBanner/ResourceCardBanner';
import CLRIcon from '@/components/CLRIcon/CLRIcon';
import AccessEventOriginLabel from '@/components/AccessEventOriginLabel/AccessEventOriginLabel';
import { AccountContext } from '@/context/account.context';
import { modalContext } from '@/context/modal.context';
import Access from '@/utils/api/odas/access';
import Bookmarks from '@/utils/api/odas/bookmarks';
import { accessEventHelpers, resourceHelpers } from '@/utils/helpers';
import t from '@/utils/Language/ResourceCard-translations';
import ReferralNetwork from '@/utils/referral_network';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import classes from './UtilizationResourceCard.module.scss';

/**
 * Plan management card for saved/utilized resources for community members.
 * Used by: PlanList
 */
const UtilizationResourceCard = ({ accessEvent }) => {
  const account = useContext(AccountContext);
  const { enqueueModal } = useContext(modalContext);
  const [orgNetwork, setOrgNetwork] = useState(false);
  const [resourceNetworkIds, setResourceNetworkIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const aeResource = accessEvent?.resource || null;

  useEffect(() => {
    if (account.data?.affiliation?.organizationReferralNetworks && aeResource) {
      resourceHelpers.checkResourceNetworkStatus(aeResource).then((matchingNetworks) => {
        if (matchingNetworks) {
          setOrgNetwork(ReferralNetwork.inspectAffiliation(account.data));
          setResourceNetworkIds(matchingNetworks.map((n) => n.id));
        }
      });
    }
  }, [account.data, aeResource]);

  const openRelatedResourcesModal = () => {
    enqueueModal({
      name: 'RelatedResourcesModal',
      resource: aeResource
    });
  };

  const removeFromPlan = () => {
    setProcessing(true);
    switch (accessEvent?.fetchable_type) {
      case 'Opportunity':
        enqueueModal({
          name: 'ConfirmationModal',
          buttonLayout: 'joined',
          buttonOrder: ['confirm', 'decline'],
          buttonStyles: {
            decline: {
              type: 'primary'
            }
          },
          onConfirm: () => {
            return Access.delete({ accessEventId: accessEvent?.id }).then(() => {
              account.updateAccessEventCollection({ action: 'remove', accessEvent });
              setProcessing(false);
              return true;
            });
          },
          onCancel: () => {
            setProcessing(false);
            return false;
          },
          title: t('Unsave resource?'),
          text: t('Are you sure you want to unsave this resource? This resource will be removed from your plan.')
        });
        break;
      case 'Organization':
        Bookmarks.delete({ fetchableType: accessEvent?.fetchable_type, fetchableId: accessEvent?.fetchable_id }).then(() => {
          account.updateBookmarkCollection({ action: 'remove', bookmark: accessEvent });
          setProcessing(false);
          return true;
        });
        break;
    }
  };

  const restoreToPlan = () => {
    setProcessing(true);
    Bookmarks.addBookmark({ fetchableType: accessEvent?.fetchable_type, fetchableId: accessEvent?.fetchable_id }).then((response) => {
      account.updateBookmarkCollection({ action: 'add', bookmark: response.data.bookmark });
      setProcessing(false);
      return true;
    });
  };

  const isDeletable = () => {
    return accessEventHelpers.isDeletable(accessEvent) || (accessEvent?.fetchable_type === 'Organization' && !accessEvent?.deleted);
  };

  const isActiveAccessEventOrOrganization = () => {
    return accessEventHelpers.isActive(accessEvent) || accessEvent?.fetchable_type === 'Organization';
  };

  const showReferralOrigin = () => {
    return accessEventHelpers.hasReferralOrigin(accessEvent) && !accessEventHelpers.utilizationQuestionAnswered(accessEvent);
  };

  const showResourceIcon = () => {
    return !accessEventHelpers.isActive(accessEvent) && accessEvent?.fetchable_type === 'Opportunity';
  };

  const showDistance = () => {
    return aeResource?.locations?.length > 0 && isActiveAccessEventOrOrganization();
  };

  const showRating = () => {
    return isActiveAccessEventOrOrganization() && !!aeResource?.rating;
  };

  const showNetworkBadge = () => {
    return resourceNetworkIds.length > 0;
  };

  const showAccessInstruction = () => {
    return (
      aeResource?.access_instructions?.length > 0 &&
      !accessEventHelpers.utilizationQuestionAnswered(accessEvent) &&
      !accessEventHelpers.isClosedLoopReferral(accessEvent)
    );
  };

  const showClrStatement = () => {
    return accessEventHelpers.isClosedLoopReferral(accessEvent) && !accessEventHelpers.utilizationQuestionAnswered(accessEvent);
  };

  const showRelatedResources = () => {
    return accessEventHelpers.utilizationQuestionAnswered(accessEvent) && accessEventHelpers.isArchived(accessEvent);
  };

  const showUtilizationQuestion = () => {
    return accessEvent?.fetchable_type === 'Opportunity' && !accessEventHelpers.isClosedLoopReferral(accessEvent) && !accessEvent?.is_common_app;
  };

  const isUnavailable = () => {
    return (
      accessEvent?.fetchable_type === 'Opportunity' && accessEventHelpers.isActive(accessEvent) && resourceHelpers.isCurrentlyUnavailable(aeResource)
    );
  };

  if (!accessEvent) {
    return null;
  }

  return (
    <Card className={classes.utilizationResourceCard}>
      {isDeletable() && (
        <>
          {processing ? (
            <span className={`px-1 ${classes.closeButton}`}>
              <FontAwesomeIcon className={`fa-spin`} icon={['fas', 'spinner']} />
            </span>
          ) : (
            <Button
              dataCy="accordion-item-button-close"
              type="link"
              shape="circle"
              icon={{ style: 'fas', font: 'times', position: 'left' }}
              additionalClasses={`${classes.closeButton} ${isUnavailable() ? classes.adjustedTop : ''}`}
              onButtonClick={removeFromPlan}
            />
          )}
        </>
      )}
      {accessEvent?.deleted && (
        <Card.Body>
          <p className="text-center mb-0">
            <FontAwesomeIcon className={classes.blueCheck} icon={['fas', 'check-circle']} /> {t('You removed this resource.')}{' '}
            {processing ? (
              <FontAwesomeIcon className={`fa-spin`} icon={['fas', 'spinner']} />
            ) : (
              <a
                href="#"
                onClick={(ev) => {
                  ev.preventDefault();
                  restoreToPlan();
                }}
                className={classes.undoLink}
              >
                {t('Undo')}
              </a>
            )}
          </p>
        </Card.Body>
      )}
      {!accessEvent?.deleted && (
        <Card.Body className={[showResourceIcon() ? classes.iconPadding : '', showReferralOrigin() ? 'pt-0' : ''].join(' ')}>
          {showResourceIcon() && (
            <div className={classes.resourceIconContainer}>
              <ResourceIcon resource={aeResource} boxed={true} />
            </div>
          )}
          {showReferralOrigin() && (
            <Row className={classes.referralUpdates}>
              <Col>
                {accessEvent?.origins.map((origin, index) => {
                  return (
                    <div key={index}>
                      <AccessEventOriginLabel accessEventOrigin={origin} />
                    </div>
                  );
                })}
              </Col>
            </Row>
          )}
          {isUnavailable() && <ResourceCardBanner>{t('Currently unavailable')}</ResourceCardBanner>}
          <Row>
            <Col>
              <h1 data-cy="accordion-item-opportunity-name" className={classes.cardTitle}>
                <a href={resourceHelpers.pathTo(aeResource)}>
                  {
                    {
                      Opportunity: aeResource?.title,
                      Organization: aeResource?.name
                    }[accessEvent?.fetchable_type]
                  }
                  <FontAwesomeIcon className={classes.chevronSpacing} icon={['fas', 'chevron-right']} />
                </a>
              </h1>
              <h2 data-cy="accordion-item-organization-name" className={classes.cardSubtitle}>
                {
                  {
                    Opportunity: <a href={resourceHelpers.pathTo(aeResource?.organization)}>{aeResource?.organization?.name}</a>,
                    Organization: aeResource?.region
                  }[accessEvent?.fetchable_type]
                }
              </h2>
            </Col>
          </Row>
          {showDistance() && (
            <Row>
              <Col>
                <DistanceTo locationList={aeResource?.locations} resource={aeResource} showIcon={true} showOtherLocations={true} />
              </Col>
            </Row>
          )}
          {showRating() && (
            <Row className="my-2">
              <Col>
                <StarRating stars={aeResource?.rating} />
              </Col>
            </Row>
          )}
          {showNetworkBadge() && (
            <Row className="my-2">
              <Col>
                {resourceNetworkIds.map((id) => (
                  <CLRIcon key={id} orgNetwork={orgNetwork} orgNetworkId={id} />
                ))}
              </Col>
            </Row>
          )}
          {(showAccessInstruction() || showClrStatement()) && (
            <Row>
              <Col>
                <WhatToDoNext accessEvent={accessEvent} numberToShow={1} showNotes={false} />
              </Col>
            </Row>
          )}
          {showRelatedResources() && (
            <Row className={classes.linkButtonRow}>
              <Col>
                <Button
                  type="link"
                  icon={{ style: 'fas', font: 'chevron-right', position: 'right' }}
                  text={t('See related resources')}
                  onButtonClick={openRelatedResourcesModal}
                />
              </Col>
            </Row>
          )}
          {showUtilizationQuestion() && (
            <Row className={classes.utilizationRow}>
              <Col className={accessEventHelpers.utilizationQuestionAnswered(accessEvent) ? '' : classes.showSeparator}>
                <UtilizationQuestion accessEvent={accessEvent} />
              </Col>
            </Row>
          )}
          {accessEvent?.fetchable_type === 'Organization' && (
            <Row className="mt-2">
              <Col>
                {/* TODO: Update this to a `next/link` when Organization Pages are Migrated to React */}
                <Button
                  type="primary"
                  text={t('Contact Info')}
                  onButtonClick={(e) => {
                    window.location = resourceHelpers.pathTo(aeResource) + '#section_contact';
                  }}
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      )}
    </Card>
  );
};

UtilizationResourceCard.propTypes = {};

export default UtilizationResourceCard;
